// 자동 알림 메일 설정 (툴바의 "⏰ 알림 Email Template" 버튼).
//
// EmailTemplateModal의 쌍둥이 — 같은 chrome, 같은 두 탭 저장 모델 — 인데 한 가지가 더
// 있다. 이 다이얼로그는 템플릿만이 아니라 "며칠에 한 번, 몇 번까지 보낼지"도 정한다.
// 둘을 갈라 두 개의 다이얼로그로 만들지 않은 이유는 CRA가 답해야 하는 질문이 하나이기
// 때문이다: "답이 없는 사이트를 어떻게 재촉할 것인가". 주기만 켜 두고 문구를 못 보거나,
// 문구만 고치고 그게 언제 나가는지 모르는 상태가 이 기능의 가장 흔한 오해다.
//
// 초대 템플릿과의 결정적 차이는 {{password}}가 없다는 것이고, 그 사실이 화면에 적혀 있다.
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import EmailComposer from "./EmailComposer";
import {
  DEFAULT_REMINDER_TEMPLATES, REMINDER_TEMPLATE_VARS, REMINDER_SAMPLE_VARS,
  hasRequiredVars, hasPasswordVar,
} from "./reminderTemplates";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn,
  LangTabs, LangTab, Loading, Warning,
} from "./emailModalUI";

const Meta = styled.div`
  font-size: 11px;
  color: #adb5bd;
  align-self: center;
  margin-right: auto;
`;

const Schedule = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  cursor: pointer;
  input { cursor: pointer; margin: 0; }
`;

// 주기 입력은 껐을 때 사라지지 않고 흐려진다: 무엇이 설정되어 있는지는 꺼 둔 동안에도
// 읽을 수 있어야 다시 켤 때 무슨 일이 벌어질지 알 수 있다.
const Cadence = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 13px;
  color: ${(p) => (p.$disabled ? "#adb5bd" : "#495057")};
  input {
    width: 58px;
    padding: 4px 6px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 13px;
    text-align: right;
    &:disabled { background: #f1f3f5; color: #adb5bd; }
  }
`;

const Note = styled.div`
  margin-top: 10px;
  font-size: 11px;
  line-height: 1.7;
  color: #6c757d;
  b { color: #495057; }
`;

const LANGS = [
  { key: "ko", label: "한국어" },
  { key: "en", label: "English" },
];

const COMPOSER_NOTE = (
  <>
    알림 메일에는 <b>비밀번호 변수 {"{{password}}"}를 쓸 수 없습니다.</b> 이 메일은 새 비밀번호를
    발급하지 않으며, 사이트는 처음 받은 초대 메일의 비밀번호를 그대로 사용합니다.
    <br />
    {"{{sentDate}}"}는 초대를 보낸 날, {"{{daysSince}}"}는 그 뒤 경과한 일수입니다.
  </>
);

const DEFAULT_SETTINGS = { enabled: false, intervalDays: 7, maxCount: 3 };

export default function ReminderTemplateModal({ sfqId, onClose, onSaved }) {
  const [lang, setLang]         = useState("ko");
  const [drafts, setDrafts]     = useState(null);  // { ko: {subject, bodyHtml}, en: {...} }
  const [meta, setMeta]         = useState({});    // { ko: {updatedBy, updatedAt}, ... }
  const [settings, setSettings] = useState(null);  // { enabled, intervalDays, maxCount }
  const [saving, setSaving]     = useState(false);

  // 템플릿과 설정은 서로 다른 라우트지만 한 화면이므로 함께 기다린다. 어느 한쪽이 실패해도
  // 다이얼로그는 열려야 한다 — 기본값만으로도 편집을 시작할 수 있고, 저장은 저장대로
  // 자기 오류를 말한다.
  useEffect(() => {
    let alive = true;

    Promise.all([
      sfqApi.getReminderTemplate(sfqId).then((r) => r.data.data || {}).catch(() => ({})),
      sfqApi.getReminderSettings(sfqId).then((r) => r.data.data || null).catch(() => null),
    ]).then(([saved, s]) => {
      if (!alive) return;
      setDrafts({
        ko: pick(saved.ko) || { ...DEFAULT_REMINDER_TEMPLATES.ko },
        en: pick(saved.en) || { ...DEFAULT_REMINDER_TEMPLATES.en },
      });
      setMeta({ ko: saved.ko, en: saved.en });
      setSettings(s || { ...DEFAULT_SETTINGS });
    });

    return () => { alive = false; };
  }, [sfqId]);

  function pick(row) {
    return row ? { subject: row.subject, bodyHtml: row.bodyHtml } : null;
  }

  function patch(next) {
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...next } }));
  }

  function patchSettings(next) {
    setSettings((s) => ({ ...s, ...next }));
  }

  function resetToDefault() {
    if (!window.confirm(`${LANGS.find((l) => l.key === lang).label} 알림 템플릿을 기본값으로 되돌립니다. 계속할까요?`)) return;
    setDrafts((d) => ({ ...d, [lang]: { ...DEFAULT_REMINDER_TEMPLATES[lang] } }));
  }

  async function handleSave() {
    const draft = drafts[lang];
    if (!draft.subject?.trim()) { alert("제목을 입력해 주세요."); return; }
    if (!hasRequiredVars(draft.bodyHtml)) {
      alert("본문에 설문 링크 변수 {{publicUrl}}가 있어야 저장할 수 있습니다.");
      return;
    }
    // API도 거부하지만, 여기서 막아야 CRA가 "왜 안 되는지"를 편집 화면에서 읽는다.
    if (hasPasswordVar(draft.bodyHtml) || hasPasswordVar(draft.subject)) {
      alert(
        "알림 메일에는 비밀번호 변수 {{password}}를 쓸 수 없습니다.\n\n" +
        "알림은 새 비밀번호를 발급하지 않으므로 서버가 채워 넣을 비밀번호가 없습니다. " +
        "사이트는 처음 받은 초대 메일의 비밀번호를 그대로 사용합니다."
      );
      return;
    }

    const days = parseInt(settings.intervalDays, 10);
    const max  = parseInt(settings.maxCount, 10);
    if (Number.isNaN(days) || days < 1 || days > 90) {
      alert("알림 주기는 1일에서 90일 사이로 입력해 주세요."); return;
    }
    if (Number.isNaN(max) || max < 1 || max > 20) {
      alert("최대 발송 횟수는 1회에서 20회 사이로 입력해 주세요."); return;
    }

    setSaving(true);
    try {
      // 설정을 먼저 저장한다. 둘 중 하나만 성공하는 경우 "주기는 저장됐는데 문구가 옛날 것"
      // 쪽이 그 반대보다 낫다 — 문구는 기본값이라도 보낼 수 있는 글이지만, 주기가 저장되지
      // 않으면 CRA는 켰다고 믿는 기능이 꺼져 있는 상태로 남는다.
      await sfqApi.saveReminderSettings(sfqId, {
        enabled: !!settings.enabled, intervalDays: days, maxCount: max,
      });
      await sfqApi.saveReminderTemplate(sfqId, {
        lang, subject: draft.subject, bodyHtml: draft.bodyHtml,
      });
      alert("알림 메일 설정이 저장되었습니다.");
      onSaved?.({ enabled: !!settings.enabled, intervalDays: days, maxCount: max });
      onClose();
    } catch (err) {
      alert(err?.response?.data?.error?.message
            || err?.response?.data?.message
            || "알림 설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }

  const savedAt = meta[lang]?.updatedAt;
  const off = !settings?.enabled;

  return (
    <Backdrop>
      <Modal $wide>
        <Header>
          <Title>자동 알림 메일</Title>
          <CloseBtn onClick={onClose} disabled={saving}>×</CloseBtn>
        </Header>

        <Body>
          {!settings || !drafts ? (
            <Loading>불러오는 중...</Loading>
          ) : (
            <>
              <Schedule>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={!!settings.enabled}
                    disabled={saving}
                    onChange={(e) => patchSettings({ enabled: e.target.checked })}
                  />
                  답변이 없는 사이트에 자동으로 알림 메일을 보낸다
                </Toggle>

                <Cadence $disabled={off}>
                  <input
                    type="number" min={1} max={90}
                    value={settings.intervalDays}
                    disabled={off || saving}
                    onChange={(e) => patchSettings({ intervalDays: e.target.value })}
                  />
                  일마다, 최대
                  <input
                    type="number" min={1} max={20}
                    value={settings.maxCount}
                    disabled={off || saving}
                    onChange={(e) => patchSettings({ maxCount: e.target.value })}
                  />
                  회까지
                </Cadence>

                <Note>
                  마지막으로 보낸 메일(초대 또는 알림)로부터 지정한 일수가 지난 사이트에만 나갑니다.
                  <b> 이미 제출한 사이트, 링크가 만료·회수된 사이트, 개별적으로 알림을 끈 사이트</b>에는
                  보내지 않으며, Study가 Approved(Active) 상태일 때만 발송됩니다.
                  사이트별로 끄거나 다른 주기를 주려면 목록에서 그 사이트의 <b>Edit</b>을 여세요.
                </Note>
              </Schedule>

              <Warning>
                알림 메일은 <b>새 비밀번호를 발급하지 않습니다.</b> 사이트는 처음 받은 초대 메일의
                비밀번호를 그대로 쓰며, 작성 중이던 답변도 그대로 보존됩니다.
                (비밀번호를 새로 주려면 목록에서 <b>Resend</b>를 쓰세요.)
              </Warning>

              <LangTabs>
                {LANGS.map((l) => (
                  <LangTab key={l.key} type="button" $active={lang === l.key} onClick={() => setLang(l.key)}>
                    {l.label}
                    {!meta[l.key] && <span title="아직 저장된 적 없음 (기본값)"> ·</span>}
                  </LangTab>
                ))}
              </LangTabs>

              <EmailComposer
                sfqId={sfqId}
                subject={drafts[lang].subject}
                bodyHtml={drafts[lang].bodyHtml}
                onChange={patch}
                vars={REMINDER_SAMPLE_VARS[lang]}
                varList={REMINDER_TEMPLATE_VARS}
                note={COMPOSER_NOTE}
              />
            </>
          )}
        </Body>

        <Footer>
          <Meta>
            {savedAt
              ? `최종 수정: ${new Date(savedAt).toLocaleString("ko-KR")}`
              : "저장된 알림 템플릿 없음 — 기본값을 표시하고 있습니다."}
          </Meta>
          <Btn type="button" onClick={resetToDefault} disabled={!drafts || saving}>기본값으로</Btn>
          <Btn type="button" onClick={onClose} disabled={saving}>닫기</Btn>
          <Btn type="button" $primary onClick={handleSave} disabled={!drafts || !settings || saving}>
            {saving ? "저장 중..." : "저장"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
